import { Box, Card, CardContent, selectClasses, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useDashboardStats } from '../../hooks/useDashboardStats'
import { TUser } from '../../shared/types/user.type'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import StatsTimeSelector from './StatsTimeSelector'
import { PERIODS } from '../../enums/StatsTimes.enum'
import { getDatesFromSelectedPeriod } from '../../shared/utils/stats'
import StatCard from './StatCard'
import { t } from 'i18next'
import { convertSpeed, convertSpeedToNb } from '../../shared/utils/conversion'
import { useAuth } from '../../hooks/useAuth'



function StatsCard() {
    const auth = useSelector<RootState>(state => state.user.user) as TUser | null
    const isUserLoading = useSelector<RootState>(state => state.user.isUserLoading) as boolean
    const [period, setSelectedPeriod] = useState(0)
    const { getMetric } = useAuth()
    const getOptions = useMemo(() => {
        let options = []
        if (auth?.id) {
            options.push(`user=${auth?.id}`)
        }
        if (period !== 0) {
            let dates = getDatesFromSelectedPeriod(period)
            Object.entries(dates).forEach(date => {
                options.push(`${encodeURIComponent(date[0])}=${encodeURIComponent(date[1])}`)
            })
        }

        return options
    }, [auth?.id, period])

    const { distanceData, distanceLoading, horseData, horseLoading, timeData, timeLoading, trainingsData, trainingsLoading } = useDashboardStats({ enabled: !isUserLoading, options: getOptions })


    const timeToHours = timeData?.count ? Math.round(timeData.count / 60) : 0

    function convertDistance(value: number, system: "metric" | "imperial" = "metric"): string {
        if (!Number.isInteger(value) || value < 0) {
            throw new Error("Value must be a positive integer representing meters.");
        }

        if (system === "metric") {
            return `${(value / 1000).toFixed(2)} km`;
        } else if (system === "imperial") {
            const miles = value / 1609.34;
            return `${miles.toFixed(2)} mi`;
        } else {
            throw new Error("Invalid system. Use 'metric' or 'imperial'");
        }
    }

    return (
        <Card>
            <CardContent>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'} gap={1}>
                    <Typography variant='h5'>{t('tabs:statistics')}</Typography>
                    <StatsTimeSelector
                        periods={PERIODS}
                        onPeriodChange={(period) => setSelectedPeriod(period)}
                        selectedPeriod={period}
                    />
                </Box>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'} gap={2} mt={3}>
                    <StatCard
                        icon='cumulativeTrainingTime'
                        title={t('key:cumulativeTrainingTime')}
                        value={timeToHours}
                        unit={t("scale:h")}
                        isLoading={timeLoading}
                    />
                    <StatCard
                        icon='horseTrained'
                        title={t('key:horseTrained')}
                        value={horseData?.count ?? 0}
                        isLoading={horseLoading}

                    />
                    <StatCard
                        icon='recordedSessions'
                        title={t('key:recordedSessions')}
                        value={trainingsData?.count ?? 0}
                        isLoading={trainingsLoading}

                    />
                    <StatCard
                        icon='direction'
                        title={t('key:cumulativeTrainingDistance')}
                        value={convertDistance(distanceData?.count ?? 0, auth?.unit_system)}
                        isLoading={distanceLoading}
            
                    />
                </Box>
            </CardContent>
        </Card>
    )
}

export default StatsCard